<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import dayjs from "dayjs";
import { useApiClient } from "@/api/client";
import { useToastStore } from "@/stores/toast";
import PriceListImportDetailsDialog from "@/modules/booking_experts/pages/Components/PriceListImportDetailsDialog.vue";
import { usePriceListImportStore } from "@/stores/priceListImportStore";
import { useCurveModelStore } from "@/modules/booking_experts/store";
import MasterPriceListImportListDO = Modules.BookingExperts.Data.MasterPriceList.MasterPriceListImportListDO;
import MasterPriceDO = Modules.BookingExperts.Data.MasterPriceList.MasterPriceDO;

const route = useRoute();
const router = useRouter();
const api = useApiClient("bookingExperts");
const importProgress = usePriceListImportStore();
const toasts = useToastStore();
const curveModelStore = useCurveModelStore();

const priceListId = computed(() => Number(route.params.id));

const priceList = ref<MasterPriceDO>();
const items = ref<MasterPriceListImportListDO[]>([]);
const isLoading = ref(false);
const itemsPerPage = ref(10);
const totalItemsLength = ref(0);
const page = ref(1);

const detailsDialogIsVisible = ref(false);
const currentImportId = ref<number>();

const headers = [
	{ title: "Started", key: "created_at", sortable: true },
	{ title: "Status", key: "status", sortable: false },
	{ title: "Prices imported", key: "imported_count", sortable: false },
	{ title: "Prices changed", key: "changed_price_count", sortable: false },
	{ title: "Finished", key: "finished_at", sortable: true },
	{ title: "Actions", key: "actions", align: "center", sortable: false },
];

const getStatusColor = (status: string) => {
	switch (status) {
		case "pending":
		case "running":
			return "primary";
		case "success":
			return "success";
		case "failed":
			return "error";
		case "skipped":
			return "secondary";
		default:
			return "primary";
	}
};

// ---------------------------------------------------------------------------
// The 1 minute cooldown is a backend rule; showing it here means the user never
// has to click to discover the import was rejected.
// ---------------------------------------------------------------------------

/** Mirrors MasterPriceListImportService::COOLDOWN_MINUTES. */
const COOLDOWN_MINUTES = 1;
const now = ref(dayjs());
let clock: ReturnType<typeof setInterval> | undefined;

const cooldownMinutesLeft = computed(() => {
	if (!priceList.value?.import_started_at) {
		return 0;
	}

	const availableAt = dayjs(priceList.value.import_started_at).add(
		COOLDOWN_MINUTES,
		"minute"
	);

	return Math.max(0, availableAt.diff(now.value, "minute") + 1);
});

const isImporting = computed(() =>
	importProgress.isImporting(priceListId.value)
);

const importButtonHint = computed(() => {
	if (isImporting.value) {
		return "An import is already running";
	}

	return cooldownMinutesLeft.value > 0
		? `Next import available in ${cooldownMinutesLeft.value} min`
		: "";
});

// ---------------------------------------------------------------------------

type SortItem = { key: string; order?: "asc" | "desc" };

const loadImports = async (options?: { sortBy?: SortItem[] }) => {
	isLoading.value = true;

	const sort = (options?.sortBy ?? [])
		.map(({ key, order }) => (order === "desc" ? `-${key}` : key))
		.join(",");

	const response = await api.getPriceListImports(priceListId.value, {
		page: page.value,
		per_page: itemsPerPage.value,
		...(sort ? { sort } : {}),
	});

	if (response.isOk()) {
		items.value = response.value.data;
		totalItemsLength.value = response.value.total;
	}

	isLoading.value = false;
};

const loadPriceList = async () => {
	const response = await api.getMasterPrice(priceListId.value);

	if (response.isOk()) {
		priceList.value = response.value;
		curveModelStore.currentBreadcrumb = response.value.name;
	}
};

const startImport = async () => {
	const result = await api.importPrices(priceListId.value);

	if (result.isErr()) {
		toasts.add({
			id: `price-list-import-${priceListId.value}-error`,
			type: "error",
			title: "Import could not be started",
			message: result.error.message ?? "Please try again.",
			dismissible: true,
			timeout: 8000,
		});

		return;
	}

	await Promise.all([loadPriceList(), loadImports()]);
};

const openDetailsDialog = (importId: number) => {
	currentImportId.value = importId;
	detailsDialogIsVisible.value = true;
};

/** The live row for an import that is still running, from the broadcast store. */
const runningProgress = computed(() => {
	const progress = importProgress.latestImport(priceListId.value);

	return progress && isImporting.value ? progress : undefined;
});

const runningStepLabel = computed(() => {
	const progress = runningProgress.value;

	if (!progress) {
		return "";
	}

	const label = progress.step_name?.replaceAll("_", " ") ?? "Preparing";

	if (progress.current_step === null) {
		return label;
	}

	return progress.step_count === null
		? `${label} · page ${progress.current_step}`
		: `${label} · ${progress.current_step} of ${progress.step_count}`;
});

// Refetch once a run reaches a terminal status, so the row becomes the stored
// record rather than the last broadcast.
watch(isImporting, (running, wasRunning) => {
	if (wasRunning && !running) {
		loadPriceList();
		loadImports();
	}
});

const openDetailsDialogFromRoute = () => {
	const importId = Number(route.params.importId);

	if (!importId) {
		return;
	}

	openDetailsDialog(importId);
	router.replace({
		name: "pricing.imports",
		params: { id: priceListId.value },
	});
};

watch(() => route.params.importId, openDetailsDialogFromRoute);

watch(priceListId, () => {
	loadPriceList();
	loadImports();
});

onMounted(() => {
	loadPriceList();
	clock = setInterval(() => (now.value = dayjs()), 30_000);

	openDetailsDialogFromRoute();
});

onUnmounted(() => {
	clearInterval(clock);
	curveModelStore.currentBreadcrumb = null;
});
</script>

<template>
	<PriceListImportDetailsDialog
		:price-list-id="priceListId"
		:import-id="currentImportId"
		v-model="detailsDialogIsVisible"
	/>

	<v-card class="mt-4 pa-4 pb-2">
		<div class="d-flex align-center justify-space-between mb-4">
			<div>
				<h3>Price imports</h3>
				<span class="text-medium-emphasis text-caption">
					Importing overwrites the existing prices of this price list.
				</span>
			</div>

			<div class="text-right">
				<v-btn
					:disabled="isImporting || cooldownMinutesLeft > 0"
					:loading="isImporting"
					@click="startImport"
				>
					<v-icon icon="fa-light fa-rotate" class="mr-2" />
					Import new prices
				</v-btn>
				<div
					v-if="importButtonHint"
					class="text-medium-emphasis text-caption mt-1"
				>
					{{ importButtonHint }}
				</div>
			</div>
		</div>

		<v-alert
			v-if="runningProgress"
			type="info"
			class="mb-4"
			:title="`Import running · ${runningStepLabel}`"
			:text="`${runningProgress.imported_count} prices imported so far`"
		/>

		<v-data-table-server
			:items="items"
			:items-length="totalItemsLength"
			:loading="isLoading"
			:headers="headers"
			v-model:page="page"
			v-model:items-per-page="itemsPerPage"
			@update:options="loadImports"
		>
			<template v-slot:item.created_at="{ item }">
				{{ dayjs(item.created_at).format("DD MMM YYYY HH:mm") }}
			</template>

			<template v-slot:item.status="{ item }">
				<v-chip
					:color="getStatusColor(item.status)"
					label
					density="comfortable"
				>
					{{ item.status }}
				</v-chip>
			</template>

			<template v-slot:item.finished_at="{ item }">
				{{
					item.finished_at
						? dayjs(item.finished_at).format("DD MMM YYYY HH:mm")
						: "-"
				}}
			</template>

			<template v-slot:item.actions="{ item }">
				<v-btn
					size="30"
					variant="text"
					@click="openDetailsDialog(item.id)"
					v-tooltip="'View details'"
					:data-testid="`view-import-${item.id}`"
				>
					<v-icon size="18" icon="fa-light fa-circle-info" />
				</v-btn>
			</template>
		</v-data-table-server>
	</v-card>
</template>

<style scoped lang="scss"></style>
